import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import i18n from '../locales/i18n';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import OfflineUtil from '../utils/OfflineUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import DatesUtil from '../utils/DatesUtil';
import TreesUtil from '../utils/TreesUtil';
import { treeStore, UnsupportedTreeFormatError } from '../utils/trees/TreeStore';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class TreesService {
    static addTree(tree, action) {
        tree.id = uuidv4();
        tree.properties.treeId = tree.id;
        tree.properties.dimensions.propertiesId = tree.id;
        const promise = Axios.post(BACKEND_ENDPOINT + 'trees/' + RealtimeUtil.getConnectionId(), tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            tree.properties = TreesUtil.normalizeTreeProperties(response.data.properties);
            console.log('#fetchId', tree.fetchId, response.data.fetchId);
            tree.fetchId = response.data.fetchId;
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            else {
                if (tree.projectId) {
                    OfflineUtil.modifyDynamicCache(BACKEND_ENDPOINT + 'trees/isMaxReached/' + tree.projectId, (body) => {
                        if (body.state) showToast('trees_limit_reached');
                        else if (body.maxElement > -1 && (body.maxElements - (body.nbElements + 1)) <= 10) {
                            body.nbElements++;
                            showToast('trees_limit_almost_reached', '(' + body.nbElements + '/' + body.maxElements + ')');
                            if (body.maxElements === body.nbElements) body.state = true;
                        } else body.nbElements++;
                        return body;
                    });
                }
                else if (tree.projectId) showToast('trees_limit_verification_failed');
            }
            return Promise.reject();
        });

        const toastId = 'tree_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'tree_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'tree_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static addTrees(trees, action) {
        trees.forEach(t => {
            t.id = uuidv4();
            t.properties.treeId = t.id;
            t.properties.dimensions.propertiesId = t.id;
        });
        const promise = Axios.post(BACKEND_ENDPOINT + 'trees/bulk/' + RealtimeUtil.getConnectionId(), trees, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.forEach(t => {
                let tree = trees.find(x => x.id === t.id);
                tree.properties = TreesUtil.normalizeTreeProperties(t.properties);
                console.log('#fetchId', tree.fetchId, t.fetchId);
                tree.fetchId = t.fetchId;
            });
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            else {
                if (trees[0].projectId) {
                    OfflineUtil.modifyDynamicCache(BACKEND_ENDPOINT + 'trees/isMaxReached/' + trees[0].projectId, (body) => {
                        if (body.state) showToast('trees_limit_reached');
                        else if (body.maxElement > -1 && (body.maxElements - (body.nbElements + trees.length)) <= 10) {
                            body.nbElements += trees.length;
                            showToast('trees_limit_almost_reached', '(' + body.nbElements + '/' + body.maxElements + ')');
                            if (body.maxElements === body.nbElements) body.state = true;
                        } else body.nbElements += trees.length;
                        return body;
                    });
                }
                else if (trees[0].projectId) showToast('trees_limit_verification_failed');
            }
            return Promise.reject();
        });

        const prefix = trees.length > 1 ? 'trees_' : 'tree_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise, trees.length);
        else showToast('will_sync');
        return promise;
    }

    static addTreeHistory(tree, date) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/history/?date=' + date.toISOString(), tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('history_added');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('history_addition_failed');
            return;
        });
    }

    static getTrees(projectId, data, { binary = AppSettings.isTreesBinaryEnabled() } = {}) {
        performance.mark(`trees-loading-${data.page}`);

        const url = (!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url)) && (data?.page || data?.page === 0)
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT) + 'trees/projects/' + projectId;

        const config = { headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` } };
        if (binary) {
            config.headers['Accept'] = 'application/octet-stream';
            config.responseType = 'arraybuffer';
        }

        return Axios.post(url, data, config).then(response => {
            performance.mark(`trees-loaded-${data.page}`);

            if (binary) {
                // Back ancien ou en-tête réécrit : réponse JSON reçue en arraybuffer
                const contentType = response.headers?.['content-type'] || response.headers?.get?.('content-type') || '';
                if (!contentType.includes('octet-stream')) {
                    const texte = new TextDecoder().decode(new Uint8Array(response.data));
                    const donnees = JSON.parse(texte);
                    if (Array.isArray(donnees?.trees))
                        for (let i = 0; i < donnees.trees.length; i++) TreesUtil.normalizeTree(donnees.trees[i]);
                    return donnees;
                }

                try {
                    return TreesService.decodeTrees(response.data);
                } catch (error) {
                    if (!(error instanceof UnsupportedTreeFormatError)) throw error;
                    // Back/front désynchronisés : repli JSON plutôt que lire les octets de travers
                    // eslint-disable-next-line no-console
                    console.warn('[Grality] Bloc binaire illisible, repli sur le JSON :', error.message);
                    return TreesService.getTrees(projectId, data, { binary: false });
                }
            }

            const trees = response.data?.trees;
            if (Array.isArray(trees))
                for (let i = 0; i < trees.length; i++) TreesUtil.normalizeTree(trees[i]);
            return response.data;
        }, () => {
            performance.mark(`trees-loaded-${data.page}`)
            if (navigator.onLine && !data?.cacheKey && !data?.cursor) showToast('trees_loading_failed');
            return;
        });
    }

    static decodeTrees(buffer) {
        const { chunk, createView } = treeStore.addChunk(buffer);
        const trees = new Array(chunk.rowCount);
        for (let row = 0; row < chunk.rowCount; row++) trees[row] = createView(row);
        return { missingCustomFieldIds: chunk.missingCustomFieldIds, trees };
    }

    static removeTrees(trees) {
        const promise = Axios.delete(BACKEND_ENDPOINT + 'trees/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: trees
        }).then(response => {
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            else {
                if (trees[0].projectId !== 0) {
                    OfflineUtil.modifyDynamicCache(BACKEND_ENDPOINT + 'trees/isMaxReached/' + trees[0].projectId, (body) => {
                        body.nbElements -= trees.length;
                        if (body.maxElement > -1 && (body.maxElements - body.nbElements) <= 10) body.state = false;
                        return body;
                    });
                }
            }
            return Promise.reject();
        });

        const prefixe = trees.length > 1 ? 'trees' : 'tree';
        if (navigator.onLine) showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        else showToast('will_sync');
        return promise;
    }

    static updateTree(id, tree, history, action, replanting) {
        const promise = Axios.put(BACKEND_ENDPOINT + 'trees/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id + '&history=' + history + '&replanting=' + replanting, tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (replanting) tree.properties.toCutDown = 0;
            tree.properties = TreesUtil.normalizeTreeProperties({ ...tree.properties, ...response.data.tree.properties });
            tree.properties.plantingDate = tree.properties.plantingDate ? DatesUtil.convertUTCDateToDate(tree.properties.plantingDate) : null;
            tree.properties.deathDate = tree.properties.deathDate ? DatesUtil.convertUTCDateToDate(tree.properties.deathDate) : null;

            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'tree_' + action;
        const successId = 'tree_' + action.replace('ing', 'ed');
        const errorId = 'tree_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static bulkUpdateTrees(trees, action) {
        const promise = Axios.put(BACKEND_ENDPOINT + 'trees/bulk/' + RealtimeUtil.getConnectionId(), trees, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let tree = trees.find(tree => tree.id === feature.id);
                    if (tree) {
                        tree.properties = TreesUtil.normalizeTreeProperties({ ...tree.properties, ...feature.properties });
                        tree.properties.plantingDate = tree.properties.plantingDate ? DatesUtil.convertUTCDateToDate(tree.properties.plantingDate) : null;
                        tree.properties.deathDate = tree.properties.deathDate ? DatesUtil.convertUTCDateToDate(tree.properties.deathDate) : null;
                    }
                });
            }
            return response;
        }, (error) => {
            if (navigator.onLine && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'trees_' + action;
        const successId = 'trees_' + action.replace('ing', 'ed');
        const errorId = 'trees_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        if (navigator.onLine) showLoadingToast(toastId, successId, errorId, promise);
        else showToast('will_sync');
        return promise;
    }

    static getTreeHistory(id) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/history/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) if (navigator.onLine) showToast('element_history_loading_failed');
            return;
        });
    }

    static getTreeCharts(id) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/charts/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) if (navigator.onLine) showToast('element_charts_loading_failed'); // TODO
            return;
        });
    }

    static getEssences(loadAll = false) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/essences' + (loadAll ? '?loadAll=true' : ''), {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, (e) => {
            if (navigator.onLine) showToast('essences_loading_failed');
            return;
        });
    }

    static getCoverTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/coverTypes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('cover_types_loading_failed');
            return;
        });
    }

    static getInteractions() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/interactions', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('interactions_loading_failed');
            return;
        });
    }

    static getMicroHabitats() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/microHabitats', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('micro_habitats_loading_failed');
            return;
        });
    }

    static getRootSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/rootSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('root_symptoms_loading_failed');
            return;
        });
    }

    static getCollarSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/collarSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('collar_symptoms_loading_failed');
            return;
        });
    }

    static getTrunkSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/trunkSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('trunk_symptoms_loading_failed');
            return;
        });
    }

    static getBranchSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/branchSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('branch_symptoms_loading_failed');
            return;
        });
    }

    static getLeafSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/leafSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('leaf_symptoms_loading_failed');
            return;
        });
    }

    static getPathogens() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/pathogens', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('pathogens_loading_failed');
            return;
        });
    }

    static getPests() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/pests', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('pests_loading_failed');
            return;
        });
    }

    static getEpiphytes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/epiphytes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('epiphytes_loading_failed');
            return;
        });
    }

    static getVigors() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/vigors', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('vigors_loading_failed');
            return;
        });
    }

    static getDeathCauses() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/deathCauses', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('death_causes_loading_failed');
            return;
        });
    }

    static getHealthReviews() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/healthReviews', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('health_reviews_loading_failed');
            return;
        });
    }

    static getOntogenicStages() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/ontogenicStages', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('ontogenic_stages_loading_failed');
            return;
        });
    }

    static getArchiStages() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/archiStages', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('archi_stages_loading_failed');
            return;
        });
    }

    static getArchiStates() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/archiStates', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('archi_states_loading_failed');
            return;
        });
    }

    static getRisks() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/risks', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('risks_loading_failed');
            return;
        });
    }

    static getTippingRisks() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/tippingRisks', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('tipping_risks_loading_failed');
            return;
        });
    }

    static getOrganCalibers() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/organCalibers', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('organ_calibers_loading_failed');
            return;
        });
    }

    static getTargets() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/targets', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('targets_loading_failed');
            return;
        });
    }

    static getTreePorts() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/treePorts', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('tree_ports_loading_failed');
            return;
        });
    }

    static getPlantationTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/plantationTypes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('plantation_types_loading_failed');
            return;
        });
    }

    static getPlantationCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/plantationCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('plantation_coefficients_loading_failed');
            return;
        });
    }

    static getSituationCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/situationCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('situation_coefficients_loading_failed');
            return;
        });
    }

    static getPatrimonialCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/patrimonialCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('patrimonial_coefficients_loading_failed');
            return;
        });
    }

    static exportTreesFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Arbres").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_xlsx_export_failed');
        });
    }

    static exportTreeAsPDF(tree, image, addPrices, photosId, colorless, toastCustomTexts, historyId = null) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportPDF/' + tree.id, { image, addPrices, photosId, colorless, historyId }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = tree.properties.projectReference ? `${i18n.t("Fiche arbre")}_${tree.properties.projectReference}` : i18n.t("Fiche arbre");
            if (tree.properties.customReference) filename += '_' + tree.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (navigator.onLine) showToast('elements_pdf_export_failed');
        });
    }

    static exportTreesAsPDF(projectId, elementIds, { projection, colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportAllPDF/' + projectId, { elementIds, projection, colorless, addPhotos }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        }, () => {
            if (navigator.onLine) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportAllBacklogsAsPDF(projectId, grouping) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/exportAllBacklogsPDF/' + grouping + '/' + projectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
            }
        }).then(response => {
            return response;
        }, () => {
            if (navigator.onLine) showToast('backlogs_pdfs_export_failed');
        });
    }

    static exportTreesAsSHP(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportSHP/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Arbres").toLowerCase()}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_shp_export_failed');
        });
    }

    static exportTreesAsGPKG(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportGPKG/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Arbres").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (navigator.onLine) showToast('elements_gpkg_export_failed');
        });
    }

    static addEssence(essence, isAdmin = false) {
        return Axios.post(BACKEND_ENDPOINT + (isAdmin ? 'admin/' : '') + 'trees/essences/', essence, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('essence_added');
            return response.data;
        }, () => {
            if (navigator.onLine) showToast('essence_addition_failed');
            return;
        });
    }

    static updateEssences(essences) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/trees/essences/', essences, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('essences_updated');
            return response.status;
        }, () => {
            if (navigator.onLine) showToast('essences_update_failed');
            return;
        });
    }

    static deleteEssences(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/trees/essences/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids: ids }
        }).then(response => {
            return response.status;
        }, () => {
            showToast(ids.length > 1 ? 'essences_deletion_failed' : 'essence_deletion_failed');
            return;
        });
    }

    static updateSpwFields(projectId, layers) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/trees/updateSpwFields/' + projectId, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const layerMap = new Map(layers.map(layer => [layer.feature.id, layer]));
            response.data.forEach(tree => {
                const layer = layerMap.get(tree.id);
                if (layer) {
                    layer.feature.properties = TreesUtil.normalizeTreeProperties({ ...layer.feature.properties, ...tree.properties });
                    layer.feature.properties.plantingDate = layer.feature.properties.plantingDate ? DatesUtil.convertUTCDateToDate(layer.feature.properties.plantingDate) : null;
                    layer.feature.properties.deathDate = layer.feature.properties.deathDate ? DatesUtil.convertUTCDateToDate(layer.feature.properties.deathDate) : null;
                }
            });

            showToast('spw_fields_updated');
            return response.data;
        }, () => {
            showToast('spw_fields_update_failed');
            return;
        });
    }
}
